import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { QuizManagementService } from '@edtech-ui/core/services/quiz-management/quiz-management.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';
import { AssessmentMetadataById } from '@edtech-ui/core/models/assesment/get-assesment-by-link';
import { ActivatedRoute, Router } from '@angular/router';
import { CreateUserAssesmentPayload } from '@edtech-ui/core/models/assesment/register-assesment-users-by-link';
import { OTPAssesmentPayload } from '@edtech-ui/core/models/assesment/assesment-otp-by-link';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';
import { LoginPayload } from '@edtech-ui/core/models/interfaces/login';
import { FullScreenService } from '../proctoring/full-screen/full-screen.service';
import { ProctoringRuleEngineServiceTsService } from '../proctoring/engine/proctoring-rule-engine.service.ts.service';
import { environment } from 'environments/environment';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../shared/confirmation-dialog/confirmation-dialog.component';


@Component({
	selector: 'app-assessment-briefing',
	standalone: true,
	imports: [CommonModule, FormsModule, ReactiveFormsModule, ConfirmationDialogComponent],
	templateUrl: './assessment-briefing.component.html',
	styleUrl: './assessment-briefing.component.scss'
})
export class AssessmentBriefingComponent implements OnInit, OnDestroy {
	isChecked = false;
	showProctoringModal = false;
	currentStep = 1;
	screenShareError = false;
	otpTimer: number = 30;
	otpInterval: any;
	verificationCode: string[] = ['', '', '', '', '', ''];
	subscription = new Subscription();
	assesmentData: AssessmentMetadataById | null = null;
	linkId: string | null = null;
	assesmentForm: FormGroup;
	userEmail = '';
	isExamReady = false;
	cameraStream: MediaStream | null = null;
	cameraAccessGranted = false;
	isResendingOtp = false;
	isFromStudentDashboard = false;
	hasToken = false;
	isFromMockTest = false;


	constructor(
		private dialog: MatDialog,
		private quizService: QuizManagementService,
		private toastr: ToastrService,
		private route: ActivatedRoute,
		private _authService: AuthServiceService,
		private router: Router,
		private fullScreenService: FullScreenService,
		private proctoringEngineService: ProctoringRuleEngineServiceTsService
	) {
		this.assesmentForm = new FormGroup({
			first_name: new FormControl('', Validators.required),
			last_name: new FormControl('', Validators.required),
			email: new FormControl('', [Validators.required, Validators.email]),
			phone: new FormControl('', [Validators.required, Validators.pattern(/^[6-9]\d{9}$/)]),
		})
	}

	isMobileDevice(): boolean {
		const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

		// 1. Standard mobile User-Agent check (handles normal mobile browsers)
		const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);

		// 2. iOS devices (iPhone/iPad) in "Desktop site" mode
		// Spoofs as Macintosh/MacIntel and has multi-touch support (> 1)
		const isIPadOS = (navigator.platform === 'MacIntel' || userAgent.includes('Macintosh')) && navigator.maxTouchPoints > 1;

		// 3. Android devices (phone/tablet) in "Desktop site" mode
		// Spoofs as Linux, has multi-touch support (> 1), and is not ChromeOS
		const isAndroidDesktopMode = navigator.maxTouchPoints > 1 &&
			userAgent.includes('Linux') &&
			!userAgent.includes('CrOS');

		// 4. Physical hardware screen dimensions check
		// Width < 768 is a phone/tablet in portrait.
		// If height < 768, we check if it is in portrait mode (width < height) to prevent blocking small laptops (e.g. 1280x720).
		const isSmallScreen = window.screen && (
			window.screen.width < 768 ||
			(window.screen.height < 768 && window.screen.width < window.screen.height)
		);

		return isMobileUA || isIPadOS || isAndroidDesktopMode || isSmallScreen;
	}

	checkDeviceAndBlock(): void {
		if (this.isMobileDevice()) {
			const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
				disableClose: true,
				data: {
					title: 'Desktop Or Laptop Required',
					message: 'This assessment must be taken on a desktop or laptop computer. Mobile, iPad, and tablet access is not supported.',
					confirmText: 'Copy Link',
					cancelText: 'Close',
					type: 'warning'
				}
			});

			dialogRef.afterClosed().subscribe((shouldCopy: boolean) => {
				if (shouldCopy) {
					navigator.clipboard.writeText(window.location.href).then(() => {
						this.toastr.success('Link copied! Please open it on a desktop browser.');
					});
				}
				//this.router.navigate(['/']);
			});
		}
	}

	ngOnInit(): void {

		this.subscription.add(
			this.route.paramMap.subscribe(paramMap => {
				this.linkId = paramMap.get('linkId');
				if (this.linkId) {
					this.getQuizAssesmentById();
				}
			})
		);

		this.subscription.add(
			this.route.queryParams.subscribe(params => {
				this.hasToken = !!localStorage.getItem('token');
				this.isFromStudentDashboard = params['isStudentDashboard'] === 'true';
				this.isFromMockTest = params['fromMockTest'] === 'true';
			})
		);

		this.fullScreenService.listenForExit();

		this.subscription.add(
			this.fullScreenService.escapeAttempt$.subscribe(() => {
				if (this.isExamReady) {
					this.toastr.warning('You exited fullscreen! Re-enter required');
					setTimeout(() => {
						this.fullScreenService.enterFullscreen();
					}, 300);
				}
			})
		);
	}


	getQuizAssesmentById() {
		if (!this.linkId) return;
		this.subscription.add(
			this.quizService.getAssesmentById(this.linkId).pipe(
				tap(response => {
					if (response.Success) {
						this.assesmentData = response.Data;
						const isProctored = this.assesmentData?.is_proctored !== false && this.assesmentData?.proctoring_config?.enable_proctoring !== false;
						if ((this.isFromMockTest || this.route.snapshot.queryParams['fromMockTest'] === 'true') && !isProctored) {
							this.finishBriefingAndNavigate();
						} else {
							this.isFromMockTest = false;
						}
					}
				}),
				catchError(() => {
					this.toastr.error('Something went wrong');
					return of(null);
				})
			).subscribe()
		)
	}


	postAssesmentRegister() {

		if (this.assesmentForm.invalid) {
			this.assesmentForm.markAllAsTouched();
			return;
		}
		const payload: CreateUserAssesmentPayload = {
			...this.assesmentForm.value
		}
		this.subscription.add(
			this.quizService.postAssesmentRegister(this.linkId!, payload).pipe(
				tap(response => {
					if (response.Success) {
						this.toastr.success(response.Message);
						this.userEmail = response.Data.email;
						this.verificationCode = ['', '', '', '', '', ''];
						this.currentStep = 2;
						this.startOtpTimer();
						setTimeout(() => {
							const firstInput = document.getElementById('code-0') as HTMLInputElement;
							if (firstInput) {
								firstInput.focus();
							}
						}, 100);
					} else {
						this.toastr.error(response.Message);
					}
				}),
				catchError(() => {
					this.toastr.error('Something wnet wrong');
					return of(null);
				})
			).subscribe()
		)
	}


	postAssesmentOtp() {
		const otpValue = this.verificationCode.join('');
		const payload: OTPAssesmentPayload = {
			email: this.userEmail,
			otp: otpValue
		}
		this.subscription.add(
			this.quizService.postAssesmentOtp(this.linkId!, payload).pipe(
				tap(response => {
					if (response.Success) {
						this.toastr.success(response.Message);
						if (response.Data?.user_id) {
							const newUserId = response.Data.user_id.toString();
							localStorage.setItem('user_id', response.Data.user_id.toString());
							this._authService.currentUserId.next(newUserId);
						}

						if (response.Data?.phone_email) {
							this._authService.currentUserEmail.next(response.Data.phone_email);
							localStorage.setItem('phone_email', response.Data.phone_email);
						}

						this._authService.setToken(response.Data.token);
						this.hasToken = true;
						this._authService.setModules(response.Data.modules);
						this._authService.setRoleId(response.Data.role_id.toString());

						if (response.Data.refresh_token) {
							localStorage.setItem('refresh_token', response.Data.refresh_token);
						}

						if (response.Data?.modules) {
							this._authService.setModules(response.Data.modules);
						}

						this.proceedToProctoringOrAssessment();
					} else {
						this.toastr.error(response.Message);
					}
				}),
				catchError(() => {
					this.toastr.error('Something wnet wrong');
					return of(null);
				})
			).subscribe()
		)
	}


	resendOtp() {
		if (!this.userEmail) {
			return;
		}

		this.isResendingOtp = true;

		this.verificationCode = ['', '', '', '', '', ''];
		this.startOtpTimer();

		const payload: LoginPayload = {
			email: this.userEmail,
			fcm_token: ''
		};

		this.subscription.add(
			this._authService.postLogin(payload).pipe(
				tap(response => {
					if (response.Success) {
						this.toastr.success(response.Message);
						setTimeout(() => {
							const firstInput = document.getElementById('code-0') as HTMLInputElement;
							firstInput.focus();
						}, 100);
					} else {
						this.toastr.error(response.Message);
					}
				}),
				finalize(() => {
					this.isResendingOtp = false;
				}),
				catchError(() => {
					this.toastr.error('Something wnet wrong');
					return of(null);
				})
			).subscribe()
		)
	}


	startOtpTimer() {
		this.otpTimer = 30;

		if (this.otpInterval) {
			clearInterval(this.otpInterval);
		}

		this.otpInterval = setInterval(() => {
			if (this.otpTimer > 0) {
				this.otpTimer--;
			} else {
				clearInterval(this.otpInterval);
			}
		}, 1000);
	}


	convertToMinSec(seconds: number): string {
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		return `${mins} min ${secs} sec`;
	}


	toggleCheck() {
		this.isChecked = !this.isChecked;
	}


	startAssessment() {
		if (this.isMobileDevice()) {
			this.checkDeviceAndBlock();
			return;
		}

		this.screenShareError = false;

		if (this.skipRegistrationSteps) {
			this.proceedToProctoringOrAssessment();
		} else {
			this.showProctoringModal = true;
			this.currentStep = 1;
		}
	}

	proceedToProctoringOrAssessment() {
		if (this.assesmentData?.is_proctored === false) {
			this.finishBriefingAndNavigate();
			return;
		}

		this.showProctoringModal = true;
		const config = this.assesmentData?.proctoring_config;
		if (config?.enable_camera === false) {
			this.currentStep = 4;
		} else {
			this.currentStep = 3;
		}
	}


	async finishBriefingAndNavigate() {
		this.showProctoringModal = false;
		this.isExamReady = true;

		await this.fullScreenService.enterFullscreen();

		const isProctored = this.assesmentData?.is_proctored !== false && this.assesmentData?.proctoring_config?.enable_proctoring !== false;
		const queryParams: any = this.skipRegistrationSteps ? { source: 'isStudentDashboard' } : {};
		if (this.route.snapshot.queryParams['fromMockTest'] === 'true') { queryParams.fromMockTest = 'true'; }

		await this.router.navigate(['/assessment', this.linkId], {
			queryParams,
			state: {
				is_proctored: isProctored,
				proctoring_config: this.assesmentData?.proctoring_config
			}
		});
	}


	editDetails() {
		this.currentStep = 1;
		this.verificationCode = ['', '', '', '', '', ''];
	}


	onCodeInput(event: Event, index: number) {
		const input = event.target as HTMLInputElement;
		const value = input.value;

		this.verificationCode[index] = value;

		if (value && index < 5) {
			const nextInput = document.getElementById(`code-${index + 1}`) as HTMLInputElement;
			if (nextInput) nextInput.focus();
		}
	}


	onCodeKeydown(event: KeyboardEvent, index: number) {
		if (event.key === 'Backspace' && !this.verificationCode[index] && index > 0) {
			const prevInput = document.getElementById(`code-${index - 1}`) as HTMLInputElement;
			if (prevInput) {
				prevInput.focus();
				this.verificationCode[index - 1] = '';
			}
		}
	}


	onCodePaste(event: ClipboardEvent, index: number) {
		event.preventDefault();
		const clipboardData = event.clipboardData;
		if (!clipboardData) return;

		const pastedText = clipboardData.getData('text').replace(/\s/g, '').trim();
		if (!pastedText) return;

		const characters = pastedText.split('').slice(0, 6);
		characters.forEach((char, i) => {
			if (i < 6) {
				this.verificationCode[i] = char;
			}
		});

		const nextIndex = Math.min(characters.length, 5);
		setTimeout(() => {
			const nextInput = document.getElementById(`code-${nextIndex}`) as HTMLInputElement;
			if (nextInput) {
				nextInput.focus();
			}
		});
	}


	trackByIndex(index: number, obj: any): any {
		return index;
	}


	private stopCameraStream(): void {
		if (!this.cameraStream) return;
		this.cameraStream.getTracks().forEach(track => track.stop());
		this.cameraStream = null;
	}


	async allowAccess() {
		try {
			this.stopCameraStream();
			this.cameraStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
			this.cameraAccessGranted = true;
		} catch (err) {
			this.toastr.error('Camera/Mic access denied');
		}
	}

	goToScreenShare() {
		this.currentStep = 4;
	}



	async shareScreen() {
		try {
			const stream = await navigator.mediaDevices.getDisplayMedia({
				video: { displaySurface: 'monitor' } as any,
				audio: false
			});

			const videoTrack = stream.getVideoTracks()[0];
			const settings = videoTrack.getSettings() as any;

			if (settings.displaySurface && settings.displaySurface !== 'monitor') {
				stream.getTracks().forEach(t => t.stop());
				this.toastr.error('Please select Entire Screen only');
				this.screenShareError = true;
				return;
			}

			this.proctoringEngineService.setScreenStream(stream);

			videoTrack.addEventListener('ended', () => {
				this.proctoringEngineService.clearScreenStream();
			});

			this.screenShareError = false;
			await this.finishBriefingAndNavigate();

		} catch (err) {
			this.screenShareError = true;
			this.toastr.error('Screen sharing required');
		}
	}


	cancelScreenShare() {
		this.screenShareError = true;
	}


	closeModal() {
		this.showProctoringModal = false;
	}

	getLogoUrl(url: string | null | undefined): string {
		if (!url) return '';
		if (url.startsWith('http') || url.startsWith('data:')) {
			return url;
		}
		const baseUrl = environment.companyApiUrl;
		return url.startsWith('/') ? `${baseUrl}${url}` : `${baseUrl}/${url}`;
	}


	// isMobileDevice(): boolean {
	// 	const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
	// 	const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
	// 	const isIPadOS = (navigator.platform === 'MacIntel' || userAgent.includes('Macintosh')) && navigator.maxTouchPoints > 1;
	// 	const isAndroidDesktopMode = navigator.maxTouchPoints > 1 && userAgent.includes('Linux') && !userAgent.includes('CrOS');
	// 	const isSmallScreen = window.screen && (window.screen.width < 768 || window.screen.height < 768);
	// 	return isMobileUA || isIPadOS || isAndroidDesktopMode || isSmallScreen;
	// }


	get skipRegistrationSteps(): boolean {
		return this.isFromStudentDashboard && this.hasToken;
	}


	ngOnDestroy(): void {
		this.subscription.unsubscribe();
		this.fullScreenService.cleanup();
		this.stopCameraStream();
	}
}
